import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import { FC } from 'react';
import { ComputedResult } from './shared/prompts/infrastructure/PromptRunner';
import { HorizontalScrollGrid, InfoCard } from '@backstage/core-components';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { scoreToStars } from './shared/scores/domain/score';
import { titleCase } from './shared/strings/strings';
import { CriteriaSet } from './shared/criteria/domain/criteria';

type TechnologyChoiceResultsProps = {
  results: ComputedResult[];
  responses: Record<string, { criteriaKey: string; value: number }>;
  criteriaSet: CriteriaSet<string>;
};

const LightTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 12,
    cursor: 'pointer',
  },
}))(Tooltip);

const useStyles = makeStyles({
  card: {
    height: '100%',
    '& div[class^="MuiCardContent-root-"]': {
      overflow: 'auto',
    },
  },
});

const TechnologyChoiceResults: FC<TechnologyChoiceResultsProps> = ({
  results,
  responses,
  criteriaSet,
}) => {
  const classes = useStyles();

  return (
    <>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Typography variant="h3" gutterBottom>
            Results:
          </Typography>
          <Typography variant="body2" gutterBottom>
            Here you have the best match choices based on your responses.
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" align="center" gutterBottom>
            <Link
              align="center"
              variant="inherit"
              href="/architecture-center/technology-choice"
            >
              <Button variant="outlined" color="primary">
                Start again
              </Button>
            </Link>
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item xs={12}>
          <HorizontalScrollGrid>
            <Box minWidth={448} maxHeight={448} m={2} p={4}>
              <Typography variant="h5" gutterBottom>
                Your responses
              </Typography>
              {Object.values(responses).map(response => (
                <Box
                  key={response.criteriaKey}
                  display="flex"
                  justifyContent="space-between"
                >
                  <LightTooltip
                    title={`${criteriaSet[response.criteriaKey].name}: ${
                      criteriaSet[response.criteriaKey].description
                    }`}
                  >
                    <Box display="flex" alignItems="flex-start">
                      <Typography variant="body1" gutterBottom>
                        {criteriaSet[response.criteriaKey].name}
                      </Typography>
                      <HelpOutlineIcon color="disabled" fontSize="inherit" />
                    </Box>
                  </LightTooltip>
                  <Typography variant="body1" gutterBottom>
                    {scoreToStars(response.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
            {results.map((result, index) => (
              <Box
                minWidth={448}
                maxHeight={448}
                m={2}
                key={result.option.name}
              >
                <InfoCard
                  title={`${index + 1}: ${result.option.name}`}
                  className={classes.card}
                  subheader={
                    <Grid container justifyContent="space-between">
                      <Grid item color="success.main">
                        <Typography variant="body1">
                          {result.option.tags?.map(tag => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box color="success.main" fontWeight={800}>
                          <Chip
                            color="primary"
                            label={`Match: ${result.percentage.toFixed(0)}%`}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  }
                  deepLink={{
                    title: 'Go to documentation',
                    link: result.option.documentationLink,
                  }}
                >
                  <>
                    <Typography variant="body1" gutterBottom>
                      {result.option.description}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Pros:
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <ul>
                        {result.option.pros?.map(pro => (
                          <li key={pro}>{pro}</li>
                        ))}
                      </ul>
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Cons:
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <ul>
                        {result.option.cons?.map(con => (
                          <li key={con}>{con}</li>
                        ))}
                      </ul>
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      Scores
                    </Typography>
                    {Object.entries(result.option.criteriaScores).map(
                      ([criteriaKey, score]) => (
                        <Box
                          key={criteriaKey}
                          display="flex"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2" gutterBottom>
                            {titleCase(criteriaKey)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            {scoreToStars(score.score.value)}
                          </Typography>
                        </Box>
                      ),
                    )}
                  </>
                </InfoCard>
              </Box>
            ))}
          </HorizontalScrollGrid>
        </Grid>
      </Grid>
    </>
  );
};

export default TechnologyChoiceResults;
